import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ApplicationDto } from 'src/app/model/business/application/ApplicationDto';
import { ApplicationTransactionDto } from 'src/app/model/business/application/ApplicationTransactionDto';
import { AuthenticationRequestDto } from 'src/app/model/business/application/AuthenticationRequestDto';
import { IdinRequestDto } from 'src/app/model/business/application/IdinRequestDto';
import { StatusResponseDto } from 'src/app/model/business/application/StatusResponseDto';
import { ValidateApplicationDataDto } from 'src/app/model/business/application/ValidateApplicationDataDto';
import { PremiumPayerDto } from 'src/app/model/business/PremiumPayerDto';
import { BeneficiaryTypeEnum } from 'src/app/model/enums/BeneficiaryTypeEnum';
import { IdinStatusEnum } from 'src/app/model/enums/IdinStatusEnum';
import { ApplicationApiService } from 'src/app/services/applicationApiService';
import { IdinService } from 'src/app/services/IdinService';
import { ValidationService } from 'src/app/services/ValidationService';
import { EnumConverterService } from 'src/app/services/EnumConverterService';
import { SessionService } from 'src/app/session-service.service';
import { AddressDto } from 'src/app/model/business/AddressDto';
import { AddressTypeEnum } from 'src/app/model/enums/AddressTypeEnum';
import { CustomerDto } from '../../../model/business/application/CustomerDto';
import { BeneficiaryDto } from '../../../model/business/application/BeneficiaryDto';
import { InsuranceDto } from '../../../model/business/application/InsuranceDto';
import { CustomerTypeEnum } from 'src/app/model/enums/CustomerTypeEnum';
import { BeneficiaryOrderEnum } from 'src/app/model/enums/BeneficiaryOrderEnum';
import { IBANDto } from 'src/app/model/business/application/IBANDto';
import { PledgeDto } from 'src/app/model/business/application/PledgeDto';
import { Constants } from 'src/app/constants';
import { OrganizationDto } from '../../../model/business/application/OrganizationDto';
import { InsuredAttributesDto } from 'src/app/model/business/calculation/InsuredAttributesDto';
import { RelationTypeEnum } from 'src/app/model/enums/RelationTypeEnum';
import { InsuredRelationEnum } from 'src/app/model/enums/InsuredRelationEnum';
import { ActivatedRoute, Router } from '@angular/router';
import NavigationSettings, { StepsORV } from '../../../components/navigation-component/navigation-settings.service';
import { ApplicationStepperLabels } from './application-page.settings';
import NavigationStepperMapper from './NavigationStepperMapper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { format } from 'date-fns';
import { ProductTypeEnum } from '../../../model/enums/ProductTypeEnum';
import { SessionOrvService } from '../../../services/session-orv.service';
import { OptionsEnum } from 'src/app/model/enums/OptionsEnum';
import { PersonDto } from 'src/app/model/business/application/PersonDto';
import { getDutchAddress } from '../../../utils/utils';

@Component({
  selector: 'app-application-page-orv',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './application-page.component.html',
  styleUrls: ['./application-page.component.css'],
})
export class ApplicationPageComponent implements OnInit, OnDestroy {
  applicationFormGroup: FormGroup;
  productType = ProductTypeEnum.ORV;
  isLinear = true;

  yesNoList = this.enumConverterService.toBaseList('OptionsEnum', OptionsEnum);
  OptionsEnum = OptionsEnum;
  CustomerTypeEnum = CustomerTypeEnum;
  ApplicationStepperLabels = ApplicationStepperLabels;

  lastOtherInsuranceChoice = {};
  isSecondInsured = true;
  showTitle: boolean;
  issuerId: string;
  idinRequestDto = new IdinRequestDto();
  timeLeft = 60;
  interval;
  resetting: boolean;

  @ViewChild('stepper') stepper;
  showStartIdentificationPage = true;
  showIdinVerificationFailedPage = false;
  showIdinVerificationCancelledPage = false;
  showIdinVerificationErrorPage = false;
  showIdentityVerifiedPage = false;
  showIdentityVerifiedThankYouPage = false;
  insuredBirthDate = '';
  insured2BirthDate = '';
  relativePath: string;

  constructor(
    private fb: FormBuilder,
    private applicationApiService: ApplicationApiService,
    private sessionService: SessionService,
    private sessionOrvService: SessionOrvService,
    private validationService: ValidationService,
    private enumConverterService: EnumConverterService,
    private router: Router,
    private navigationSettings: NavigationSettings,
    private idinService: IdinService,
    private activatedRoute: ActivatedRoute
  ) {
    if (this.sessionService.getFirstInsured()?.dateOfBirth) {
      this.insuredBirthDate = format(this.sessionService.getFirstInsured().dateOfBirth, 'dd-MM-yyyy');
    }
    if (this.sessionService.getSecondInsured()?.dateOfBirth) {
      this.insured2BirthDate = format(this.sessionService.getSecondInsured().dateOfBirth, 'dd-MM-yyyy');
    }

    const {
      productType,
      locale,
    } = this.activatedRoute.snapshot.params;

    this.relativePath = locale ?
      `/${locale}/${productType}` :
      `/${productType}`;

    this.resetting = false;
  }

  ngOnInit(): void {
    this.showTitle = true;
    this.initializeForm();
    this.setInitialValues();
    this.setupDynamicValidations();
  }

  setActiveStep(): void {
    this.navigationSettings.setActiveState(StepsORV.PERSONAL_DATA, true);
  }

  ngOnDestroy(): void {
    if (!this.resetting) {
      this.saveApplicationData();
    }
    this.navigationSettings.setActiveState(StepsORV.PERSONAL_DATA, false);
  }

  saveApplicationData(): void {
    this.sessionOrvService.setApplicationFormValues(this.applicationFormGroup.value);
  }

  setInitialValues(): void {
    const values = this.sessionOrvService.getApplicationFormValues();
    if (values) {
      this.applicationFormGroup.patchValue(values);
    }
  }

  setupDynamicValidations(): void {
    const insured2FormGroup = this.applicationFormGroup.get(
      'insured2'
    ) as FormGroup;

    this.applicationFormGroup
      .get('isInsured2')
      .valueChanges.subscribe((isInsured2) => {
      if (isInsured2 === OptionsEnum.No) {
        this.validationService.clearFormGroupValidators(insured2FormGroup);
      } else {
        this.validationService.setInsured2Validators(insured2FormGroup);
      }
      insured2FormGroup.updateValueAndValidity();
    });

    insured2FormGroup
      .get('addressSameAsFirstInsured')
      .valueChanges.subscribe((sameAddress) => {
      this.validationService.setInsured2ValidatorsForSameAddressChanged(insured2FormGroup, sameAddress);
      insured2FormGroup.updateValueAndValidity();
    });

    if (this.sessionService.getSecondInsured()) {
      this.validationService.setInsured2Validators(insured2FormGroup);
    }

    this.setupPolicyHolder1DynamicValidations();
    this.setupPolicyHolder2DynamicValidations();

    this.setupPremiumValidations();

    this.setUpOtherInsuranceValidation('otherInsurances1');
    this.setUpOtherInsuranceValidation('otherInsurances2');
    this.setUpEmailVerificationValidation('insured');
    if (this.sessionService.getSecondInsured()) {
      this.setUpEmailVerificationValidation('insured2');
    }
  }

  setUpEmailVerificationValidation(insuredFormGroupName: string): void {
    const insuredFormGroup = this.applicationFormGroup.get(insuredFormGroupName);
    const confirmedEmailAddress = insuredFormGroup.get('confirmedEmailAddress');
    insuredFormGroup
      .get('emailAddress')
      .valueChanges.subscribe(() => {
      confirmedEmailAddress.updateValueAndValidity();
    });
  }

  setupPremiumValidations(): void {
    const premiumPayerFrmGrp = this.applicationFormGroup.get('premiumPayer');
    premiumPayerFrmGrp
      .get('premiumPayerEqualsPolicyHolder')
      .valueChanges.subscribe((equalPolicyHolder) => {
      if (equalPolicyHolder === OptionsEnum.No) {
        this.validationService.setInsuredValidators(
          premiumPayerFrmGrp.get('premiumPayer') as FormGroup
        );
      } else {
        this.validationService.clearFormGroupValidators(
          premiumPayerFrmGrp.get('premiumPayer') as FormGroup
        );
      }
      premiumPayerFrmGrp.updateValueAndValidity();
    });
  }

  setupPolicyHolder1DynamicValidations(): void {
    const policyHolder = this.applicationFormGroup.get(
      'policyHolder'
    ) as FormGroup;

    policyHolder
      .get('sameAsInsured')
      .valueChanges.subscribe((sameAsInsured) => {
      if (sameAsInsured === OptionsEnum.No) {
        this.setUpPolicyHolder1Validations(
          policyHolder,
          policyHolder.value.policyHolderType
        );
      } else {
        this.validationService.cleanPolicyHolderValidations(policyHolder);
      }
      policyHolder.updateValueAndValidity();
    });

    policyHolder
      .get('policyHolderType')
      .valueChanges.subscribe((policyType) => {
      this.setUpPolicyHolder1Validations(policyHolder, policyType);
      policyHolder.updateValueAndValidity();
    });
  }

  setUpPolicyHolder1Validations(
    policyHolder: FormGroup,
    policyType: CustomerTypeEnum
  ): void {
    this.validationService.cleanPolicyHolderValidations(policyHolder);
    if (policyType === CustomerTypeEnum.PERSON) {
      this.validationService.setInsuredValidators(
        policyHolder.get('insured') as FormGroup
      );
    } else {
      policyHolder.get('organizationName').setValidators(Validators.required);
    }
  }

  setupPolicyHolder2DynamicValidations(): void {
    const policyHolder = this.applicationFormGroup.get(
      'policyHolder2'
    ) as FormGroup;

    policyHolder
      .get('isPolicyHolder')
      .valueChanges.subscribe((isPolicyHolder) => {
      if (
        isPolicyHolder === OptionsEnum.Yes &&
        policyHolder.value.sameAsInsured === OptionsEnum.No
      ) {
        this.validationService.setInsuredValidators(
          policyHolder.get('insured') as FormGroup
        );
      } else {
        this.validationService.cleanPolicyHolderValidations(policyHolder);
      }
      policyHolder.updateValueAndValidity();
    });

    policyHolder
      .get('sameAsInsured')
      .valueChanges.subscribe((sameAsInsured) => {
      if (sameAsInsured === OptionsEnum.No) {
        this.validationService.setInsuredValidators(
          policyHolder.get('insured') as FormGroup
        );
      } else {
        this.validationService.cleanPolicyHolderValidations(policyHolder);
      }
      policyHolder.updateValueAndValidity();
    });
  }

  setUpOtherInsuranceValidation(insurance: string): void {
    this.applicationFormGroup
      .get(insurance)
      .get('insurerHasAnotherInsurer')
      .valueChanges.subscribe((otherInsurance) => {
      const otherInsuranceFormGroup = this.applicationFormGroup.get(
        insurance
      ) as FormGroup;

      if (otherInsurance === this.lastOtherInsuranceChoice[insurance]) {
        return;
      }
      this.lastOtherInsuranceChoice[insurance] = otherInsurance;

      if (otherInsurance === OptionsEnum.No) {
        this.validationService.clearFormGroupValidators(
          otherInsuranceFormGroup
        );
      } else {
        this.validationService.setOtherInsurancesValidations(
          otherInsuranceFormGroup
        );
      }
      otherInsuranceFormGroup.updateValueAndValidity();
    });
  }

  private initializeForm(): void {
    const secondInsured = this.sessionService.getSecondInsured();
    this.isSecondInsured = secondInsured != null;
    this.applicationFormGroup = this.fb.group({
      insured: this.initializeInsuredFormGroup(
        this.sessionService.getFirstInsured()
      ),
      isInsured2: new FormControl(
        this.isSecondInsured ? OptionsEnum.Yes : OptionsEnum.No,
        Validators.required
      ),
      insured2: this.initializeInsuredFormGroup(secondInsured),
      policyHolder: this.initializePolicyHolderFormGroup(OptionsEnum.Yes),
      policyHolder2: this.initializePolicyHolderFormGroup(OptionsEnum.No),
      premiumPayer: this.initializePremiumPayerFormGroup(),
      beneficiaries: this.initializeBeneficiariesFormGroup(),
      pledgee: this.initializePledgee(),
      otherInsurances1: this.initializeOtherInsurancesFormGroup(''),
      otherInsurances2: this.initializeOtherInsurancesFormGroup(this.isSecondInsured ? '' : OptionsEnum.No),
      purposeOfTheInsurance: this.initializePurposeOfTheInsurance()
    });

    this.validationService.setInsuredValidators(
      this.applicationFormGroup.get('insured') as FormGroup
    );
  }

  initializePledgee(): FormGroup {
    return this.fb.group({
      isPledged: new FormControl(OptionsEnum.No),
      pledgeeName: new FormControl(),
      deedOfPledgeReceived: new FormControl(),
      partiallyPledgedAmount: new FormControl(),
      loanNumber: new FormControl(),
    });
  }

  initializeInsuredFormGroup(insured: InsuredAttributesDto): FormGroup {
    return this.fb.group({
      initials: new FormControl(),
      middleName: new FormControl(),
      surname: new FormControl(),
      gender: new FormControl(),
      dateOfBirth: new FormControl(insured?.dateOfBirth),
      zipCode: new FormControl(),
      houseNumber: new FormControl(),
      houseNumberSuffix: new FormControl(),
      street: new FormControl(),
      city: new FormControl(),
      mobilePhoneNo: new FormControl('+31 (0)'),
      emailAddress: new FormControl(),
      confirmedEmailAddress: new FormControl(),
      nationality: new FormControl('NL'),
      iban: new FormControl(''),
      addressSameAsFirstInsured: new FormControl(OptionsEnum.Yes),
    });
  }

  initializePolicyHolderFormGroup(isPolicyHolder: OptionsEnum): FormGroup {
    return this.fb.group({
      isPolicyHolder: new FormControl(isPolicyHolder),
      sameAsInsured: new FormControl(OptionsEnum.Yes),
      relationPolicyHolderToInsured: new FormControl(RelationTypeEnum.PARTNER),
      relationPolicyHolderToInsuredOtherText: new FormControl(),
      insured: this.initializeInsuredFormGroup(null),
      policyHolderType: new FormControl(CustomerTypeEnum.PERSON),
      organizationName: new FormControl(),
      organizationChamberOfCommerceNumber: new FormControl(),
      premiumSplit: new FormControl(OptionsEnum.Yes),
    });
  }

  initializePremiumPayerFormGroup(): FormGroup {
    return this.fb.group({
      premiumPayerEqualsPolicyHolder: new FormControl(OptionsEnum.Yes),
      premiumPayer: this.initializeInsuredFormGroup(null),
      premiumSplit: new FormControl(OptionsEnum.Yes),
      premiumPayerIBAN: new FormControl(''),
    });
  }

  initializeBeneficiariesFormGroup(): FormGroup {
    return this.fb.group({
      beneficiaryOrder: new FormControl(
        BeneficiaryOrderEnum.DEFAULT,
        Validators.required
      ),
      beneficiary1: this.initializeBeneficiaryFormGroup(
        Constants.DEFAULT_BENEFICIARY_ORDER[0]
      ),
      beneficiary2: this.initializeBeneficiaryFormGroup(
        Constants.DEFAULT_BENEFICIARY_ORDER[1]
      ),
      beneficiary3: this.initializeBeneficiaryFormGroup(
        Constants.DEFAULT_BENEFICIARY_ORDER[2]
      ),
      beneficiary4: this.initializeBeneficiaryFormGroup(
        Constants.DEFAULT_BENEFICIARY_ORDER[3]
      ),
    });
  }
  initializeBeneficiaryFormGroup(usageOrder: BeneficiaryTypeEnum): FormGroup {
    return this.fb.group({
      relationToInsured: new FormControl(),
      initials: new FormControl(),
      middleName: new FormControl(),
      surname: new FormControl(),
      gender: new FormControl(),
      dateOfBirth: new FormControl(),
      usageOrder: new FormControl(usageOrder),
    });
  }
  initializeOtherInsurancesFormGroup(value: string | OptionsEnum): FormGroup {
    return this.fb.group({
      insurerHasAnotherInsurer: new FormControl(value, Validators.required),
      tafInsurer: new FormControl(OptionsEnum.Yes),
      nameInsurer: new FormControl({ value: 'TAF', disabled: true }),
      dateOfCommencement: new FormControl(),
      cancellationDate: new FormControl(new Date()),
      sumAssured: new FormControl(),
      willCancelPolicy: new FormControl(),
    });
  }
  initializePurposeOfTheInsurance(): FormGroup {
    return this.fb.group({
      mortgageRelated: new FormControl('', Validators.required)
    });
  }

  fillPremiumPayerDtoFromFormGroup(
    premiumPayerFrmGrp: FormGroup
  ): PremiumPayerDto {
    const premiumPayerDto = new PremiumPayerDto();
    premiumPayerDto.premiumSplit = premiumPayerFrmGrp.value.premiumSplit;
    premiumPayerDto.premiumPayerIBAN =
      premiumPayerFrmGrp.value.premiumPayerIBAN;

    return premiumPayerDto;
  }

  getPerson(insured: FormGroup): PersonDto {
    const person = new PersonDto();
    person.initials = insured.value.initials;
    person.middleName = insured.value.middleName;
    person.surname = insured.value.surname;
    person.gender = insured.value.gender;
    person.dateOfBirth = insured.value.dateOfBirth;
    person.address = getDutchAddress(insured.value);

    person.phoneNumber = `0${insured.value.mobilePhoneNo}`;
    person.emailAddress = insured.value.emailAddress;
    person.nationality = insured.value.nationality;
    return person;
  }

  getOrganization(insured: FormGroup): OrganizationDto {
    const organization = new OrganizationDto();

    organization.name = insured.value.organizationName;
    organization.chamberOfCommerceNumber =
      insured.value.organizationChamberOfCommerceNumber;

    return organization;
  }

  getPolicyHolder(policyHolder: FormGroup): CustomerDto {
    if (policyHolder === undefined || policyHolder == null) {
      return null;
    }

    if (policyHolder.value.isPolicyHolder === OptionsEnum.No) {
      return null;
    }

    const customer = new CustomerDto();
    customer.customerType = policyHolder.value.policyHolderType;

    if (customer.customerType === CustomerTypeEnum.PERSON) {
      customer.person = this.getPerson(
        policyHolder.get('insured') as FormGroup
      );
    } else {
      customer.organization = this.getOrganization(policyHolder);
    }

    return customer;
  }

  getBeneficiaries(beneficiariesFrmGrp: FormGroup): BeneficiaryDto[] {
    if (
      beneficiariesFrmGrp.value.beneficiaryOrder ===
      BeneficiaryOrderEnum.DEFAULT
    ) {
      return null;
    }
    let beneficiariesDto = [];
    for (let i = 1; i <= 4; i++) {
      beneficiariesDto = this.addedBeneficiary(
        beneficiariesDto,
        'beneficiary' + i,
        beneficiariesFrmGrp,
        i
      );
    }

    return beneficiariesDto;
  }

  addedBeneficiary(
    beneficiariesDto: BeneficiaryDto[],
    beneficiary: string,
    beneficiariesFrmGrp: FormGroup,
    index: number
  ): BeneficiaryDto[] {
    const beneficiaryFrmGrp = beneficiariesFrmGrp.get(beneficiary) as FormGroup;

    if (beneficiaryFrmGrp.value.usageOrder === BeneficiaryTypeEnum.NONE) {
      return beneficiariesDto;
    }
    const beneficiaryDto = new BeneficiaryDto();
    beneficiaryDto.beneficiaryOrderEnum = BeneficiaryOrderEnum.CUSTOM;
    beneficiaryDto.relationToInsured = beneficiaryFrmGrp.value.usageOrder;
    beneficiaryDto.orderNumber = index;

    if (beneficiaryDto.relationToInsured !== BeneficiaryTypeEnum.OTHER) {
      beneficiaryDto.customer = null;
    } else {
      beneficiaryDto.customer = new CustomerDto();

      beneficiaryDto.customer.customerType = CustomerTypeEnum.PERSON;
      const person = new PersonDto();
      person.initials = beneficiaryFrmGrp.value.initials;
      person.middleName = beneficiaryFrmGrp.value.middleName;
      person.surname = beneficiaryFrmGrp.value.surname;
      person.gender = beneficiaryFrmGrp.value.gender;
      person.dateOfBirth = beneficiaryFrmGrp.value.dateOfBirth;
      person.address = null;
      person.phoneNumber = undefined;
      person.email = undefined;
      person.nationality = undefined;
      beneficiaryDto.customer.person = person;
    }

    beneficiariesDto.push(beneficiaryDto);

    return beneficiariesDto;
  }

  getBeneficiaryDtoFromFormGroup(beneficiaryFrmGrp: FormGroup): BeneficiaryDto {
    const beneficiaryDto = new BeneficiaryDto();

    beneficiaryDto.beneficiaryOrderEnum = BeneficiaryOrderEnum.CUSTOM;
    beneficiaryDto.relationToInsured = beneficiaryFrmGrp.value.usageOrder;

    beneficiaryDto.customer = new CustomerDto();

    beneficiaryDto.customer.customerType = CustomerTypeEnum.PERSON;
    beneficiaryDto.customer.person = this.getPerson(beneficiaryFrmGrp);

    return beneficiaryDto;
  }

  getPledgee(pledgeeFormGroup: FormGroup): PledgeDto {
    const pledgee = new PledgeDto();
    pledgee.pledgeeName = pledgeeFormGroup.value.pledgeeName;
    pledgee.deedOfPledgeReceived =
      pledgeeFormGroup.value.deedOfPledgeReceived === OptionsEnum.Yes;
    pledgee.partiallyPledgedAmount =
      pledgeeFormGroup.value.partiallyPledgedAmount;
    pledgee.loanNumber = pledgeeFormGroup.value.loanNumber;
    return pledgee;
  }

  getOtherInsurances(otherInsurancesFrmGrp: FormGroup): InsuranceDto {
    if ( otherInsurancesFrmGrp.value.insurerHasAnotherInsurer === OptionsEnum.No ) {
      return null;
    }

    const otherInsuranceDto = new InsuranceDto();

    otherInsuranceDto.insurerHasAnotherInsurer =
      otherInsurancesFrmGrp.value.insurerHasAnotherInsurer === OptionsEnum.Yes;
    otherInsuranceDto.tafInsurer =
      otherInsurancesFrmGrp.value.tafInsurer === OptionsEnum.Yes;
    otherInsuranceDto.nameInsurer =
      otherInsurancesFrmGrp.value.nameInsurer || 'TAF';
    otherInsuranceDto.dateOfCommencement =
      otherInsurancesFrmGrp.value.dateOfCommencement;
    otherInsuranceDto.sumAssured = otherInsurancesFrmGrp.value.sumAssured;
    otherInsuranceDto.willCancelPolicy =
      otherInsurancesFrmGrp.value.willCancelPolicy === OptionsEnum.Yes;

    if (otherInsurancesFrmGrp.value.willCancelPolicy === OptionsEnum.Yes) {
      otherInsuranceDto.cancellationDate =
        otherInsurancesFrmGrp.value.cancellationDate;
    } else {
      otherInsuranceDto.cancellationDate = null;
    }

    return otherInsuranceDto;
  }

  isPremiumPayerSplit(premiumPayerFrmGrp: FormGroup): boolean {
    return premiumPayerFrmGrp.value.premiumSplit === OptionsEnum.Yes;
  }

  isPremiumPayerEqualWithPolicyHolder(premiumPayerFrmGrp: FormGroup): boolean {
    return (
      premiumPayerFrmGrp.value.premiumPayerEqualsPolicyHolder ===
      OptionsEnum.Yes
    );
  }

  getIbanAccount(insured: FormGroup): string {
    return insured.value.iban.replace(/\s/g, '');
  }

  getPremiumPayer(premiumPayerFormGroup: FormGroup): CustomerDto {
    if (this.isPremiumPayerEqualWithPolicyHolder(premiumPayerFormGroup)) {
      return null;
    }

    const customer = new CustomerDto();
    customer.customerType = CustomerTypeEnum.PERSON;
    customer.person = this.getPerson(
      premiumPayerFormGroup.get('premiumPayer') as FormGroup
    );

    return customer;
  }

  getApplicationDto(): ApplicationDto {
    const app = new ApplicationDto();
    app.intermediary = {
      organisationId: this.sessionService.getIntermediaryId(),
      organisationName: 'REMOVE THIS HARDCODED',
      email : 'why@remove.this',
      address : new AddressDto(),
      subagentId : 0,
      telephone : '',
    };
    app.insured1 = this.getPerson(
      this.applicationFormGroup.get('insured') as FormGroup
    );

    const firstInsuredFromCalculation = this.sessionService.getFirstInsured();

    app.insured1.isSmoker = firstInsuredFromCalculation.isSmoker;

    app.insured2 =
      this.applicationFormGroup.value.isInsured2 === OptionsEnum.Yes
        ? this.getPerson(this.applicationFormGroup.get('insured2') as FormGroup)
        : null;

    if (app.insured2) {
      const secondInsuredFromCalculation = this.sessionService.getSecondInsured();
      app.insured2.isSmoker = secondInsuredFromCalculation.isSmoker;
      if (this.applicationFormGroup.get('insured2').value.addressSameAsFirstInsured === OptionsEnum.Yes) {
        app.insured2.address = app.insured1.address;
      }
    }

    const policyHolder1FormGr = this.applicationFormGroup.get(
      'policyHolder'
    ) as FormGroup;

    if (policyHolder1FormGr.value.sameAsInsured === OptionsEnum.Yes) {
      app.policyHolder1 = null;
    } else {
      app.policyHolder1 = this.getPolicyHolder(policyHolder1FormGr);
    }

    if (
      app.policyHolder1 &&
      app.policyHolder1.customerType === CustomerTypeEnum.PERSON
    ) {
      app.relationPolicyHolder1ToInsured1 =
        policyHolder1FormGr.value.relationPolicyHolderToInsured;
      app.relationPolicyHolder1ToInsured1OtherText =
        policyHolder1FormGr.value.relationPolicyHolderToInsuredOtherText;
    }

    const policyHolder2FormGr = this.applicationFormGroup.get(
      'policyHolder2'
    ) as FormGroup;

    if (policyHolder2FormGr.value.isPolicyHolder === OptionsEnum.Yes) {
      app.policyHolder2SameAsInsured2 = true;
    } else {
      app.policyHolder2SameAsInsured2 = false;
      app.policyHolder2 = this.getPolicyHolder(policyHolder2FormGr);
    }

    if (
      app.policyHolder2 &&
      app.policyHolder2.customerType === CustomerTypeEnum.PERSON
    ) {
      app.relationPolicyHolder2ToInsured2 =
        policyHolder2FormGr.value.relationPolicyHolderToInsured;
      app.relationPolicyHolder2ToInsured2OtherText =
        policyHolder2FormGr.value.relationPolicyHolderToInsuredOtherText;
    }

    app.premiumSplit = this.isPremiumPayerSplit(
      this.applicationFormGroup.get('policyHolder2') as FormGroup
    );

    app.premiumPayer = this.getPremiumPayer(
      this.applicationFormGroup.get('premiumPayer') as FormGroup
    );

    app.bankAccount = new IBANDto();
    app.bankAccount.iban = this.getIbanAccount(
      this.applicationFormGroup.get('insured') as FormGroup
    );

    const beneficiaries = this.getBeneficiaries(
      this.applicationFormGroup.get('beneficiaries') as FormGroup
    );

    app.standardBeneficiary = beneficiaries == null;
    app.beneficiaries = beneficiaries;

    app.pledged =
      this.applicationFormGroup.get('pledgee').value.isPledged ===
      OptionsEnum.Yes;
    app.pledge = app.pledged
      ? this.getPledgee(this.applicationFormGroup.get('pledgee') as FormGroup)
      : null;

    app.insurances = [];
    const otherInsurance1 = this.getOtherInsurances(
      this.applicationFormGroup.get('otherInsurances1') as FormGroup
    );
    if (otherInsurance1 != null) {
      otherInsurance1.insuredRelation = InsuredRelationEnum.INSURED;
      app.insurances.push(otherInsurance1);
    }

    const otherInsurance2 = this.getOtherInsurances(
      this.applicationFormGroup.get('otherInsurances2') as FormGroup
    );

    if (otherInsurance2 != null) {
      otherInsurance2.insuredRelation = InsuredRelationEnum.INSURED2;
      app.insurances.push(otherInsurance2);
    }

    const calculationRequest = this.sessionService.getCallculationRequest();
    this.sessionService.getCalculationResponse.subscribe(value => {
      app.calculationResponse = value;
    });
    calculationRequest.policyTerms = app.calculationResponse.policyTerms;
    app.calculationRequest = calculationRequest;
    app.dateOfCommencement = calculationRequest.dateOfCommencement;
    app.mortgageRelated = this.applicationFormGroup.get('pledgee').value.mortgageRelated === OptionsEnum.Yes;
    app.insured1Premium = app.calculationResponse.insured1premium;
    if (app.calculationResponse.insured2premium) {
      app.insured2Premium = app.calculationResponse.insured2premium;
    }

    return app;
  }

  applyForInsurance(): void {
    this.applicationFormGroup.markAllAsTouched();
    if (this.applicationFormGroup.valid) {
      const transactionApplicationDto = new ApplicationTransactionDto();
      transactionApplicationDto.applicationDto = this.getApplicationDto();
      transactionApplicationDto.merchantReference = this.idinRequestDto.merchantReference;
      this.applicationApiService
        .sendApplicationRequest(transactionApplicationDto)
        .subscribe((result) => {
          this.showIdentityVerifiedPage = false;
          this.showIdentityVerifiedThankYouPage = true;
          this.stepper.next();
      });
    }
  }

  goToStartIdentificationPage(): void {
    this.applyForInsurance();
  }

  nextFromFirstInsured(): void {
    this.applicationFormGroup.get('insured').markAllAsTouched();
    if (!this.isSecondInsured && this.applicationFormGroup.get('insured').status === 'VALID') {
      this.setPersonalDataStepAsFinished();
    }
    this.saveApplicationData();
    this.stepper.next();
  }

  nextFromSecondInsured(): void {
    this.applicationFormGroup.get('insured2').markAllAsTouched();
    if (this.applicationFormGroup.get('insured2').status === 'VALID') {
      this.setPersonalDataStepAsFinished();
    }
    this.saveApplicationData();
    this.stepper.next();
  }

  nextFromBeneficiary(): void {
    this.applicationFormGroup.get('beneficiaries').markAllAsTouched();
    this.saveApplicationData();
    this.stepper.next();
  }

  nextFromOtherInsurances(): void {
    this.applicationFormGroup.get('otherInsurances1').markAllAsTouched();
    this.applicationFormGroup.get('otherInsurances2').markAllAsTouched();
    if (this.applicationFormGroup.get('otherInsurances1').valid && this.applicationFormGroup.get('otherInsurances2').valid) {
      this.setInsuranceDataStepAsFinished();
      this.saveApplicationData();
      this.stepper.next();
    }
  }

  redirectToCalculationPage(): void {
    this.resetApplication();
    this.router.navigate([`${this.relativePath}/calculation`, this.sessionService.getIntermediaryId()]);
  }

  sendAuthenticationRequest(): void {
    if (this.issuerId !== undefined) {
      this.postAuthenticateRequest(this.issuerId);
    }
  }

  onSelectedIdinIssuer(issuerId: string): void {
    this.issuerId = issuerId;
  }

  postAuthenticateRequest(issuerId: string): void {
    const generatedUUID = Constants.uuidv4();
    const authenticationRequestDto: AuthenticationRequestDto = {
      issuerId,
      entranceCode: generatedUUID.replace(/-/g, ''),
      merchantReturnUrl: this.getMerchantUrl(),
    };

    this.idinService
      .postAuthenticateRequest(authenticationRequestDto)
      .subscribe((idinRequestResponse) => {
        if (idinRequestResponse.error) {
          this.showIdinVerificationErrorPage = true;
          this.showStartIdentificationPage = false;
        } else {
          const authRedirect = window.open();
          this.createIDINtransaction(idinRequestResponse, authRedirect);
        }
      });
  }

  checkIdxAndEntranceCodeParameters(): boolean {
    return (
      localStorage.getItem('trxid') !== null &&
      localStorage.getItem('ec') !== null &&
      localStorage.getItem('status') !== null
    );
  }

  createIDINtransaction(idinRequestResponse: IdinRequestDto, authRedirect: Window): void {
    this.idinRequestDto.issuerAuthenticationURL =
      idinRequestResponse.issuerAuthenticationURL;
    this.idinRequestDto.transactionCreateDateTimestamp =
      idinRequestResponse.transactionCreateDateTimestamp;

    if (idinRequestResponse.issuerAuthenticationURL === 'GoToDemoPage'){
      authRedirect.location.href = this.getDemoPage();
    } else {
      authRedirect.location.href = idinRequestResponse.issuerAuthenticationURL;
    }

    this.clearCache();
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        if (this.checkIdxAndEntranceCodeParameters()) {
          this.sendStatusRequest(localStorage.getItem('trxid'));
          this.saveUrlParameters();
          this.sendIdinTransaction();
          clearInterval(this.interval);
          return;
        }
      } else {
        this.timeLeft = 60;
      }
    }, 1000);
  }

  clearCache(): void {
    localStorage.removeItem('trxid');
    localStorage.removeItem('ec');
    localStorage.removeItem('status');
  }
  saveUrlParameters(): void {
    this.idinRequestDto.transactionId = localStorage.getItem('trxid');
    this.idinRequestDto.entranceCode = localStorage.getItem('ec');
  }

  sendIdinTransaction(): void {
    this.idinRequestDto.merchantReference = localStorage.getItem('ec');
    this.idinService.postIdinTransaction(this.idinRequestDto).subscribe();
  }

  private sendStatusRequest(transactionID: string): void {
    this.idinService.getStatusResponse(transactionID).subscribe((response) => {
      this.showStartIdentificationPage = false;
      this.testDemoPage(response);
      if (response.error) {
        this.showIdinVerificationErrorPage = true;
      } else if (IdinStatusEnum.CANCELLED === response.status) {
        this.showIdinVerificationCancelledPage = true;
      } else if (IdinStatusEnum.FAILURE === response.status) {
        this.showIdinVerificationFailedPage = true;
      } else if (
        IdinStatusEnum.EXPIRED === response.status ||
        IdinStatusEnum.OPEN === response.status
      ) {
        this.showStartIdentificationPage = true;
      } else if (IdinStatusEnum.SUCCESS === response.status) {
        const validateApplicationDataDto = this.createValidateApplicationDataDto(
          response
        );
        this.validateApplicationData(validateApplicationDataDto);
      }
    });
  }

  testDemoPage(response: StatusResponseDto): void {
    response.error = false;
    if (this.isIdinCancelled()) {
      response.status = IdinStatusEnum.CANCELLED;
    } else if (this.isIdinError()) {
      response.error = true;
    } else if (this.isIdinFailed()) {
      response.status = IdinStatusEnum.FAILURE;
    } else if (this.isIdinExpired()) {
      response.status = IdinStatusEnum.EXPIRED;
    } else if (this.isIdinOpen()) {
      response.status = IdinStatusEnum.OPEN;
    }else if (this.isIdinSuccess){
      response.status = IdinStatusEnum.SUCCESS;
    }
  }

  isIdinSuccess(): boolean {
    return localStorage.getItem('status') === 'Success';
  }
  isIdinFailed(): boolean {
    return localStorage.getItem('status') === 'Failed';
  }
  isIdinCancelled(): boolean {
    return localStorage.getItem('status') === 'Cancelled';
  }
  isIdinError(): boolean {
    return localStorage.getItem('status') === 'Error';
  }
  isIdinExpired(): boolean {
    return localStorage.getItem('status') === 'Expired';
  }
  isIdinSuccessButMismatch(): boolean {
    return localStorage.getItem('status') === 'Mismatch';
  }
  isIdinOpen(): boolean {
    return localStorage.getItem('status') === 'Open';
  }

  getDemoPage(): string {
    const currentURL = window.location.href;
    let tempHref = '';
    if (currentURL.includes('/applicationrequester')) {
      tempHref = currentURL.split('/#')[0];
    } else {
      tempHref = window.location.origin;
    }
    return tempHref.concat('/#/idinDemo');
  }

  private createValidateApplicationDataDto(statusResponseDto: StatusResponseDto): ValidateApplicationDataDto {
    const validateApplicationDataDto = new ValidateApplicationDataDto();
    validateApplicationDataDto.applicationDto = this.getApplicationDto();
    validateApplicationDataDto.statusResponseDto = statusResponseDto;
    return validateApplicationDataDto;
  }

  private validateApplicationData(validateApplicationDataDto: ValidateApplicationDataDto): void {
    this.idinService
      .validateApplicationDataRequest(validateApplicationDataDto)
      .subscribe((response) => {
        if (response.validData) {
          this.showIdentityVerifiedPage = true;
          this.applyForInsurance();
        } else {
          this.showIdinVerificationFailedPage = true;
        }
      });
  }

  getMerchantUrl(): string {
    const currentURL = window.location.href;
    let configHref = '';
    if (currentURL.includes('/applicationrequester')) {
      configHref = currentURL.split('/#')[0];
    } else {
      configHref = window.location.origin;
    }
    return configHref.concat('/#/idinVerificationIssuerResponse');
  }

  setPersonalDataStepAsFinished(): void {
    this.navigationSettings.setActiveState(StepsORV.PERSONAL_DATA, false);
    this.navigationSettings.setFinishedState(StepsORV.PERSONAL_DATA, true);
  }

  setInsuranceDataStepAsFinished(): void {
    this.navigationSettings.setActiveState(StepsORV.INSURANCE_DATA, false);
    this.navigationSettings.setFinishedState(StepsORV.INSURANCE_DATA, true);
  }

  stepperChangeHandler(e: StepperSelectionEvent): void {
    this.navigationSettings.setActiveState(
      NavigationStepperMapper[e.previouslySelectedStep.label],
      false,
    );
    this.navigationSettings.setActiveState(
      NavigationStepperMapper[e.selectedStep.label],
      true,
    );
  }

  resetApplication(): void {
    this.resetting = true;
    this.navigationSettings.reset();
    this.sessionService.resetCalculationResponse();
    this.sessionService.resetCalculationRequest();
    this.sessionOrvService.clearApplicationFormValues();
    this.sessionOrvService.clearCalculationFormValues();
  }

  goBack(): void {
    this.stepper.previous();
  }
}
